import React, { useState, useEffect, useRef } from "react";
import { Form } from "react-bootstrap";
import Select from "react-select";

import { FormHelper } from "utils";
import { GridView } from "components";
import { Perfil } from "models/Perfil";
import useMessage from "hooks/useMessage";
import { PerfilManager, FuncionManager } from "managers";
import useExceptionHandler from "hooks/useExceptionHandler";
import SectionRow from "components/PantallasEdit/SectionRow/SectionRow";
import EditSection from "components/PantallasEdit/EditSection/EditSection";
import InputContainer from "components/PantallasEdit/InputContainer/InputContainer";
import usePaginationValuesWhere from "hooks/usePaginationValuesWhere";
import { Simplebar } from "components/Simplebar/Simplebar";

export default function PerfilEdit({
  showLoading,
  hideLoading,
  IdSec,
  esClonar = false,
  showCanvas,
  setShowCanvas,
  updateList = () => {},
}) {
  const [perfil, setPerfil] = useState({});
  const [esNuevo, setEsNuevo] = useState(true);
  const [tituloHeader, setTituloHeader] = useState("Crear Perfil");
  const [funcionesPorAgregarList, setFuncionesPorAgregarList] = useState([]);
  const [funcionesPorAgregarGrid, setFuncionesPorAgregarGrid] = useState([]);
  const [where, setWhere] = useState({});
  const [pagination, setPagination] = useState({ EsPaginable: false });
  const { setPaginationValuesWhere } = usePaginationValuesWhere();
  const { Message, cerrarAlertas } = useMessage();
  const { handleException } = useExceptionHandler();
  const ref = useRef(null);
  const ESTATUS_LIST = Perfil.ESTATUS_LIST;

  useEffect(() => {
    if (!showCanvas) return;
    setEsNuevo(!IdSec || esClonar);
    if (IdSec) {
      showLoading();
      cargarPerfil(IdSec);
      esClonar
        ? setTituloHeader("Clonar Perfil")
        : setTituloHeader("Editar Perfil");
    } else {
      setTituloHeader("Crear Perfil");
      setDefaultValues();
      setFuncionesPorAgregarList([]);
      setFuncionesPorAgregarGrid([]);
    }
    return () => {
      hideLoading();
      cerrarAlertas();
    };
  }, [IdSec, showCanvas]);

  useEffect(() => {
    if (Object.keys(where).length > 0) {
      setFuncionesPorAgregarGrid(filterList());
    } else {
      setFuncionesPorAgregarGrid([...funcionesPorAgregarList]);
    }
  }, [where, funcionesPorAgregarList]);

  const setDefaultValues = () => {
    setPerfil({
      Estatus: Perfil.DEFAULT_ESTATUS_VALUE,
      LlamadaCondicion: Perfil.CONDICION_LLAMADA_EN_PROCESO,
    });
  };

  const cargarPerfil = (Id) => {
    PerfilManager.buscarPorId(Id)
      .then((data) => {
        setPerfil(data);
        cargarFuncionesList(data.IdSec, data.Funciones);
        setWhere({});
      })
      .catch((e) => handleException(e))
      .finally(() => hideLoading());
  };

  const grabar = () => {
    showLoading();
    if (esNuevo) {
      PerfilManager.crear(perfil)
        .then((res) => {
          setEsNuevo(false);
          Message({ message: `Grabado Exitosamente` });
          cargarPerfil(res.IdSec);
          updateList();
        })
        .catch((e) => handleException(e))
        .finally(() => hideLoading());
    } else {
      PerfilManager.actualizar(perfil)
        .then(() => {
          Message({ message: `Actualizado Exitosamente` });
          cargarPerfil(perfil.IdSec);
          updateList();
        })
        .catch((e) => handleException(e))
        .finally(() => hideLoading());
    }
  };

  function cargarFuncionesList(Id, funcionesList) {
    FuncionManager.buscarNoEnPerfilId(Id)
      .then((res) => {
        if (res && res.Headers) {
          setPaginationValuesWhere(res.Headers, pagination, setPagination);
        }
        let list = res.concat(funcionesList);
        funcionesList.map((el) => {
          let index = list.findIndex((funcion) => funcion.IdSec === el.IdSec);
          if (index !== -1) {
            list[index].EsAnadirPerfil = true;
          } else {
            list[index].EsAnadirPerfil = false;
          }
        });
        sortPermisosList(list);
      })
      .catch((e) => handleException(e))
      .finally(() => hideLoading());
  }

  const filterList = () => {
    return [...funcionesPorAgregarList].filter((el) => {
      return (
        (where.IdSec
          ? el.IdSec?.toString().includes(where.IdSec?.toString())
          : true) &&
        (where.Nombre
          ? el.Nombre?.toLowerCase().includes(where.Nombre?.toLowerCase())
          : true) &&
        (where.Descripcion
          ? el.Descripcion?.toLowerCase().includes(
              where.Descripcion?.toLowerCase()
            )
          : true)
      );
    });
  };

  function sortPermisosList(list) {
    if (!list) {
      list = [...funcionesPorAgregarList];
    }
    list.sort((a, b) => Number(b.EsAnadirPerfil) - Number(a.EsAnadirPerfil));
    setFuncionesPorAgregarList(list);
    setFuncionesPorAgregarGrid(list);
  }

  function addToList(Id) {
    const index = funcionesPorAgregarList.findIndex(
      (funcion) => funcion.IdSec === Id
    );
    perfil.Funciones.push(funcionesPorAgregarList[index]);
  }

  function removeFromList(Id) {
    const index = perfil.Funciones.findIndex((f) => f.IdSec === Id);
    setFuncionesPorAgregarList([
      ...funcionesPorAgregarList,
      perfil.Funciones[index],
    ]);
    perfil.Funciones.splice(index, 1);
  }

  function handleFuncionSwitch({ Id }) {
    const index = funcionesPorAgregarList.findIndex(
      (funcion) => funcion.IdSec === Id
    );
    let list = [...funcionesPorAgregarList];
    if (list[index].EsAnadirPerfil) {
      removeFromList(Id);
    } else {
      addToList(Id);
    }
    list[index].EsAnadirPerfil = !funcionesPorAgregarList[index].EsAnadirPerfil;
    setFuncionesPorAgregarList(list);
  }

  const FuncionesGrid = (
    <GridView
      pagination={pagination}
      setPagination={setPagination}
      allowPaging={pagination.EsPaginable}
      pageSize={pagination.CantidadPorPagina}
      pageIndex={pagination.IndiceDePagina}
      lastPageNumber={pagination.PaginasTotal}
      dataSource={funcionesPorAgregarGrid}
      where={where}
      setWhere={setWhere}
    >
      <GridView.Column
        headerText="Id"
        dataField="IdSec"
        sortExpression="IdSec"
      />
      <GridView.Column
        headerText="Nombre"
        dataField="Nombre"
        sortExpression="Nombre"
      />
      <GridView.Column
        headerText="Descripción"
        dataField="Descripcion"
        sortExpression="Descripcion"
      />
      <GridView.Column
        isField
        bodyRenderer={(row) => (
          <Form.Check
            type="switch"
            name="AgregarPerfil"
            onChange={() => handleFuncionSwitch({ Id: row.IdSec })}
            checked={row.EsAnadirPerfil}
            id={`switch-${row.IdSec}`}
          />
        )}
      />
    </GridView>
  );

  return (
    <Simplebar
      ref={ref}
      backdrop="static"
      confirmBack
      placement="end"
      size="xxl"
      portal={false}
      visible={showCanvas}
      setShow={(e) => setShowCanvas(e)}
      useLoading={true}
      title={tituloHeader}
      btnLeft={{
        show: true,
        message: "Grabar",
        isComponent: true,
        size: "45px",
        height: "24px",
        with: "24px",
        iconName: "save",
        isCenter: true,
        func: grabar,
      }}
      btnRight={{
        show: false,
        label: "Grabar",
        func: () => {},
      }}
    >
      <Form>
        <EditSection title="Información General">
          <SectionRow>
            <InputContainer label="Nombre">
              <Form.Control
                name="Nombre"
                type="text"
                onChange={(e) => FormHelper.handleChange(e, setPerfil)}
                value={perfil.Nombre}
              />
            </InputContainer>
            <InputContainer label="Descripción">
              <Form.Control
                onChange={(e) => FormHelper.handleChange(e, setPerfil)}
                name="Descripcion"
                type="text"
                value={perfil.Descripcion}
              />
            </InputContainer>
          </SectionRow>
          <SectionRow>
            <InputContainer label="Estatus">
              <Select
                isDisabled={esNuevo}
                className="react-select primary"
                classNamePrefix="react-select"
                name="Estatus"
                onChange={(e) => setPerfil({ ...perfil, Estatus: e.value })}
                options={ESTATUS_LIST}
                value={
                  esNuevo
                    ? ESTATUS_LIST.filter(
                        (o) => o.value === Perfil.DEFAULT_ESTATUS_VALUE
                      )
                    : ESTATUS_LIST.filter((o) => o.value === perfil.Estatus)
                }
              />
            </InputContainer>
          </SectionRow>
        </EditSection>
        {!esNuevo && (
          <EditSection title="Permisos">{FuncionesGrid}</EditSection>
        )}
      </Form>
    </Simplebar>
  );
}
